/**
 * Focus Pack panel — activated / latent / excluded dispositions with the
 * campaign's privacy guarantee rendered as structure, not as discipline.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05A, over C02C's
 * `HostedFocusPackStore` (`auteur_cloud/brain/focus_pack.py`).
 *
 * PRIVACY PROPERTY (required): excluded and latent records show NO source
 * text. This panel enforces that twice:
 *
 *   1. BY TYPE — `FocusDispositionRecord` (api/brain.ts) has no
 *      content-bearing field, mirroring a table with none of the
 *      `DISPOSITION_FIELDS` columns. There is nothing to render.
 *   2. BY GUARD — `RENDERABLE_DISPOSITION` names the only disposition the
 *      panel may expand beyond metadata, and the render path checks
 *      `record.disposition !== 'activated'` before touching anything more
 *      than identifiers, kind, reason code, and timestamps.
 *
 * REASON COPY COMES FROM THE SERVER, NOT FROM A RE-COPIED TABLE
 *   C02C seam S-3: bind to `reason_code_copy()` / `FOCUS_REASON_COLUMNS`
 *   (sixteen codes, not twelve). This component renders the `reason_copy`
 *   string the server already produced and the `reason_code` it assigned;
 *   it contains no local reason-code table to drift out of parity.
 *
 * VISUAL ORCHESTRATION (Builder Round P2): the panel states its own wiring
 * — Loading / Error / Unavailable — before it says anything about packs,
 * and every section is labelled so a reader can see what was looked up.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import {
  BrainApiError,
  FocusDispositionRecord,
  FocusPackView,
  fetchFocusPack,
} from '../../api/brain';

export interface FocusPackPanelProps {
  projectId: string;
}

/**
 * The ONLY disposition whose rows may be expanded beyond pure metadata.
 * Latent and excluded rows are metadata-only by construction.
 */
const RENDERABLE_DISPOSITION = 'activated' as const;

function DispositionRow({ record }: { record: FocusDispositionRecord }): React.ReactElement {
  // Guard: anything not activated renders identifiers, kind, reason, and
  // time ONLY. No source text exists on the type, and this branch is the
  // code-level second lock.
  if (record.disposition !== RENDERABLE_DISPOSITION) {
    return (
      <li className="fp-row fp-row-metadata-only" data-disposition={record.disposition}>
        <code className="fp-record-id">{record.record_id}</code>
        <span className="fp-kind">{record.kind}</span>
        <span className="fp-reason-code">{record.reason_code}</span>
        <span className="fp-reason-copy">{record.reason_copy}</span>
        <time className="fp-decided-at" dateTime={record.decided_at}>{record.decided_at}</time>
      </li>
    );
  }
  return (
    <li className="fp-row fp-row-activated" data-disposition={record.disposition}>
      <code className="fp-record-id">{record.record_id}</code>
      <span className="fp-kind">{record.kind}</span>
      <span className="fp-reason-code">{record.reason_code}</span>
      <span className="fp-reason-copy">{record.reason_copy}</span>
      <time className="fp-decided-at" dateTime={record.decided_at}>{record.decided_at}</time>
    </li>
  );
}

function DispositionList({
  title,
  records,
  note,
}: {
  title: string;
  records: FocusDispositionRecord[];
  note: string;
}): React.ReactElement {
  return (
    <section className={`fp-section fp-section-${title.toLowerCase()}`}>
      <h4>{title}</h4>
      <p className="fp-note">{note}</p>
      {records.length === 0 ? (
        <p className="fp-empty">None recorded.</p>
      ) : (
        <ul className="fp-list">
          {records.map((record) => (
            <DispositionRow key={record.record_id} record={record} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function FocusPackPanel({ projectId }: FocusPackPanelProps): React.ReactElement {
  const [pack, setPack] = useState<FocusPackView | null>(null);
  const [state, setState] = useState<'loading' | 'error' | 'unavailable' | 'ready'>('loading');

  const load = useCallback(() => {
    setState('loading');
    fetchFocusPack(projectId)
      .then((result) => {
        setPack(result);
        setState('ready');
      })
      .catch((error: unknown) => {
        if (error instanceof BrainApiError && error.status === 404) {
          setState('unavailable');
        } else {
          setState('error');
        }
      });
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (state === 'loading') {
    return <div className="fp-state fp-state-loading">Loading Focus Pack…</div>;
  }
  if (state === 'unavailable') {
    return (
      <div className="fp-state fp-state-unavailable">
        Focus Pack unavailable — the brain routes are not mounted at this deployment
        (C02C reports mounted: false). Nothing is fabricated here.
      </div>
    );
  }
  if (state === 'error' || pack === null) {
    return (
      <div className="fp-state fp-state-error">
        Focus Pack could not be loaded. <button type="button" onClick={load}>Retry</button>
      </div>
    );
  }

  return (
    <div className="fp-panel" aria-label="Focus Pack dispositions">
      <header className="fp-header">
        <h3>Focus Pack</h3>
        <p className="fp-compiled-at">Compiled <time dateTime={pack.compiled_at}>{pack.compiled_at}</time></p>
        <p className="fp-privacy-note">
          Latent and excluded entries show record identifiers, reason codes, and
          timestamps only — no source text is stored for them, so none can be shown.
        </p>
      </header>
      <DispositionList
        title="Activated"
        records={pack.activated}
        note="Records the pack carries into runs."
      />
      <DispositionList
        title="Latent"
        records={pack.latent}
        note="Indexed but not carried. Metadata only; source text is not retained here."
      />
      <DispositionList
        title="Excluded"
        records={pack.excluded}
        note="Deliberately out of scope. Metadata only; source text is not retained here."
      />
    </div>
  );
}

export default FocusPackPanel;
